<template>
  <v-card flat class="tag-manager">
    <v-card-text class="pa-4 pa-sm-6">
      <!-- Header -->
      <div class="d-flex align-center mb-5">
        <div>
          <div class="text-h6 font-weight-bold">{{ $t("tags.manage_tags") }}</div>
          <div class="caption grey--text mt-1">{{ $t("tags.manage_tags_description") }}</div>
        </div>
        <v-spacer />
        <v-btn color="primary" depressed small @click="showAddGroupDialog = true">
          <v-icon small left>mdi-plus</v-icon>
          {{ $t("tags.add_group") }}
        </v-btn>
      </div>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" style="border-radius: 4px" />

      <!-- Tag Groups -->
      <div class="tag-groups-list">
        <div
          v-for="group in tagGroups"
          :key="group.id"
          class="tag-group-card mb-4"
          :style="{ '--group-color': group.color }"
        >
          <!-- Group header -->
          <div class="tag-group-card__header d-flex align-center pa-3 px-4">
            <div class="tag-group-card__color-dot mr-3" :style="{ background: group.color }" @click="openGroupColorPicker(group, $event)" />
            <div class="flex-grow-1">
              <div class="d-flex align-center">
                <span class="font-weight-medium body-2">{{ groupDisplayName(group) }}</span>
                <v-chip v-if="group.system" x-small class="ml-2" color="blue-grey lighten-4" text-color="blue-grey darken-2">
                  <v-icon x-small left>mdi-lock</v-icon>{{ $t("tags.system_group") }}
                </v-chip>
                <v-chip v-if="group.allow_multiple" x-small class="ml-1" color="teal lighten-5" text-color="teal darken-2">
                  <v-icon x-small left>mdi-tag-multiple</v-icon>{{ $t("tags.allow_multiple") }}
                </v-chip>
              </div>
              <div class="caption grey--text">{{ (group.tags || []).length }} {{ $t("tags.tags_count") }}</div>
            </div>
            <v-btn icon small @click="editGroup(group)" v-if="!group.system">
              <v-icon small>mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn icon small @click="confirmDeleteGroup(group)" v-if="!group.system">
              <v-icon small color="grey">mdi-delete-outline</v-icon>
            </v-btn>
          </div>

          <!-- Tags -->
          <div class="tag-group-card__body pa-3 px-4">
            <div class="d-flex flex-wrap align-center" style="gap: 6px">
              <v-chip
                v-for="tag in (group.tags || [])"
                :key="tag.id"
                :color="tag.color"
                :text-color="isLightColor(tag.color) ? '#333' : '#fff'"
                small
                close
                @click:close="confirmDeleteTag(group, tag)"
                @click="editTag(group, tag)"
                class="tag-chip"
              >
                {{ tag.name }}
              </v-chip>

              <!-- Inline add tag -->
              <div v-if="addingTagGroupId === group.id" class="d-inline-flex align-center">
                <v-text-field
                  ref="newTagInput"
                  v-model="newTagName"
                  dense
                  hide-details
                  solo
                  flat
                  :placeholder="$t('tags.tag_name')"
                  class="tag-inline-input"
                  @keyup.enter="submitNewTag(group)"
                  @keyup.esc="cancelAddTag"
                  @blur="submitNewTag(group)"
                  autofocus
                />
              </div>
              <v-btn v-else x-small text color="primary" @click="startAddTag(group)" class="tag-add-btn">
                <v-icon x-small left>mdi-plus</v-icon>{{ $t("tags.add_tag") }}
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="!loading && tagGroups.length === 0" class="text-center py-12">
        <v-icon size="64" color="grey lighten-1">mdi-tag-off-outline</v-icon>
        <div class="subtitle-1 grey--text mt-4">{{ $t("tags.no_groups") }}</div>
        <v-btn color="primary" depressed small class="mt-4" @click="showAddGroupDialog = true">
          <v-icon small left>mdi-plus</v-icon>
          {{ $t("tags.add_group") }}
        </v-btn>
      </div>
    </v-card-text>

    <!-- Add Group Dialog -->
    <v-dialog v-model="showAddGroupDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold">{{ $t("tags.add_group") }}</v-card-title>
        <v-card-text class="pb-0">
          <v-text-field
            v-model="newGroupName"
            :label="$t('tags.group_name')"
            outlined
            dense
            autofocus
            @keyup.enter="submitNewGroup"
          />
          <v-select
            v-model="newGroupTaggableType"
            :items="taggableTypeOptions"
            item-text="text"
            item-value="value"
            :label="$t('tags.taggable_type')"
            outlined
            dense
          />
          <div class="d-flex align-center mb-2">
            <span class="caption grey--text mr-3">{{ $t("tags.color") }}</span>
            <div class="d-flex flex-wrap" style="gap: 6px">
              <div
                v-for="c in quickColors"
                :key="c"
                class="color-swatch"
                :class="{ 'color-swatch--active': newGroupColor === c }"
                :style="{ background: c }"
                @click="newGroupColor = c"
              />
            </div>
          </div>
          <v-switch v-model="newGroupAllowMultiple" :label="$t('tags.allow_multiple')" dense hide-details class="mt-0" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showAddGroupDialog = false">{{ $t("custom.cancel") }}</v-btn>
          <v-btn color="primary" depressed :disabled="!newGroupName.trim()" @click="submitNewGroup">{{ $t("custom.save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Group Dialog -->
    <v-dialog v-model="showEditGroupDialog" max-width="480" persistent>
      <v-card v-if="editingGroup">
        <v-card-title class="subtitle-1 font-weight-bold">{{ $t("tags.edit_group") }}</v-card-title>
        <v-card-text class="pb-0">
          <v-text-field
            v-model="editingGroup.name"
            :label="$t('tags.group_name')"
            outlined
            dense
            autofocus
          />
          <div class="d-flex align-center mb-2">
            <span class="caption grey--text mr-3">{{ $t("tags.color") }}</span>
            <div class="d-flex flex-wrap" style="gap: 6px">
              <div
                v-for="c in quickColors"
                :key="c"
                class="color-swatch"
                :class="{ 'color-swatch--active': editingGroup.color === c }"
                :style="{ background: c }"
                @click="editingGroup.color = c"
              />
            </div>
          </div>
          <v-switch v-model="editingGroup.allow_multiple" :label="$t('tags.allow_multiple')" dense hide-details class="mt-0" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showEditGroupDialog = false">{{ $t("custom.cancel") }}</v-btn>
          <v-btn color="primary" depressed @click="submitEditGroup">{{ $t("custom.save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Tag Dialog -->
    <v-dialog v-model="showEditTagDialog" max-width="480" persistent>
      <v-card v-if="editingTag">
        <v-card-title class="subtitle-1 font-weight-bold">{{ editingTag.name }}</v-card-title>
        <v-card-text class="pb-0">
          <v-text-field
            v-model="editingTag.name"
            :label="$t('tags.tag_name')"
            outlined
            dense
            autofocus
          />
          <div class="d-flex align-center mb-2">
            <span class="caption grey--text mr-3">{{ $t("tags.color") }}</span>
            <div class="d-flex flex-wrap" style="gap: 6px">
              <div
                v-for="c in quickColors"
                :key="c"
                class="color-swatch"
                :class="{ 'color-swatch--active': editingTag.color === c }"
                :style="{ background: c }"
                @click="editingTag.color = c"
              />
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showEditTagDialog = false">{{ $t("custom.cancel") }}</v-btn>
          <v-btn color="primary" depressed @click="submitEditTag">{{ $t("custom.save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="360">
      <v-card>
        <v-card-title class="subtitle-1">{{ deleteDialogTitle }}</v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showDeleteDialog = false">{{ $t("custom.cancel") }}</v-btn>
          <v-btn color="error" depressed @click="confirmDelete">{{ $t("custom.delete") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Group Color Picker -->
    <v-menu v-model="groupColorMenu" :position-x="colorMenuX" :position-y="colorMenuY" absolute offset-y :close-on-content-click="false">
      <v-color-picker
        v-if="colorPickerGroup"
        v-model="colorPickerGroup.color"
        hide-inputs
        show-swatches
        :swatches="swatches"
        @input="updateGroup(colorPickerGroup)"
      />
    </v-menu>
  </v-card>
</template>

<script>
export default {
  name: "TagGroupManager",
  data() {
    return {
      tagGroups: [],
      loading: false,
      // Add group
      showAddGroupDialog: false,
      newGroupName: "",
      newGroupColor: "#2196F3",
      newGroupAllowMultiple: false,
      newGroupTaggableType: "Car",
      // Edit group
      showEditGroupDialog: false,
      editingGroup: null,
      // Inline add tag
      addingTagGroupId: null,
      newTagName: "",
      submittingTag: false,
      // Edit tag
      showEditTagDialog: false,
      editingTag: null,
      editingTagGroupId: null,
      // Delete confirm
      showDeleteDialog: false,
      deleteDialogTitle: "",
      deleteCallback: null,
      // Color picker
      groupColorMenu: false,
      colorMenuX: 0,
      colorMenuY: 0,
      colorPickerGroup: null,
      // Palette
      quickColors: [
        "#4CAF50", "#2196F3", "#FF9800", "#9C27B0",
        "#F44336", "#00BCD4", "#795548", "#607D8B",
        "#E91E63", "#3F51B5", "#009688", "#FF5722",
      ],
      swatches: [
        ["#4CAF50", "#8BC34A", "#CDDC39"],
        ["#2196F3", "#03A9F4", "#00BCD4"],
        ["#FF9800", "#FF5722", "#F44336"],
        ["#9C27B0", "#673AB7", "#3F51B5"],
        ["#795548", "#607D8B", "#757575"],
      ],
    };
  },
  computed: {
    authToken() {
      return this.$store.getters.getAuthToken;
    },
    taggableTypeOptions() {
      return [
        { value: "Car", text: this.$t("tags.taggable_car") },
        // { value: "Booking", text: this.$t("tags.taggable_booking") },
        // { value: "Client", text: this.$t("tags.taggable_client") },
      ];
    },
  },
  mounted() {
    this.loadTagGroups();
  },
  methods: {
    isLightColor(hex) {
      if (!hex) return true;
      const c = hex.replace("#", "");
      const r = parseInt(c.substring(0, 2), 16);
      const g = parseInt(c.substring(2, 4), 16);
      const b = parseInt(c.substring(4, 6), 16);
      return (r * 299 + g * 587 + b * 114) / 1000 > 160;
    },

    groupDisplayName(group) {
      if (group.system) {
        const key = "tags.system_" + group.name;
        const translated = this.$t(key);
        return translated !== key ? translated : group.name;
      }
      return group.name;
    },

    // === Load ===
    loadTagGroups() {
      this.loading = true;
      this.axios
        .get("/api/v1/tag_groups", { headers: { Authorization: this.authToken } })
        .then((response) => {
          const data = response.data.data || [];
          this.tagGroups = data.map((item) => {
            const attrs = item.attributes || {};
            const includedTags = (response.data.included || [])
              .filter((inc) => inc.type === "tag" && item.relationships && item.relationships.tags &&
                item.relationships.tags.data.some((r) => r.id === inc.id))
              .map((inc) => ({ id: parseInt(inc.id), ...inc.attributes }))
              .sort((a, b) => a.sort_order - b.sort_order);
            return { id: parseInt(item.id), ...attrs, tags: includedTags };
          });
        })
        .catch((error) => {
          this.$notify(this.$t("errors.error"), error.response?.data?.error || error.message, "error");
        })
        .finally(() => { this.loading = false; });
    },

    // === Group CRUD ===
    submitNewGroup() {
      if (!this.newGroupName.trim()) return;
      this.axios
        .post("/api/v1/tag_groups", {
          name: this.newGroupName.trim(),
          color: this.newGroupColor,
          allow_multiple: this.newGroupAllowMultiple,
          taggable_type: this.newGroupTaggableType,
          sort_order: this.tagGroups.length,
        }, { headers: { Authorization: this.authToken } })
        .then(() => {
          this.showAddGroupDialog = false;
          this.newGroupName = "";
          this.newGroupColor = "#2196F3";
          this.newGroupAllowMultiple = false;
          this.newGroupTaggableType = "Car";
          this.loadTagGroups();
        })
        .catch((error) => {
          this.$notify(this.$t("errors.error"), error.response?.data?.errors?.join(", ") || error.message, "error");
        });
    },

    editGroup(group) {
      this.editingGroup = { ...group };
      this.showEditGroupDialog = true;
    },

    submitEditGroup() {
      const g = this.editingGroup;
      this.axios
        .patch(`/api/v1/tag_groups/${g.id}`, {
          name: g.name, color: g.color, allow_multiple: g.allow_multiple, sort_order: g.sort_order,
        }, { headers: { Authorization: this.authToken } })
        .then(() => {
          this.showEditGroupDialog = false;
          this.loadTagGroups();
        })
        .catch((error) => {
          this.$notify(this.$t("errors.error"), error.response?.data?.errors?.join(", ") || error.message, "error");
        });
    },

    updateGroup(group) {
      this.axios
        .patch(`/api/v1/tag_groups/${group.id}`, {
          name: group.name, color: group.color, allow_multiple: group.allow_multiple, sort_order: group.sort_order,
        }, { headers: { Authorization: this.authToken } })
        .catch((error) => {
          this.$notify(this.$t("errors.error"), error.response?.data?.errors?.join(", ") || error.message, "error");
          this.loadTagGroups();
        });
    },

    confirmDeleteGroup(group) {
      this.deleteDialogTitle = this.$t("tags.confirm_delete_group");
      this.deleteCallback = () => {
        this.axios
          .delete(`/api/v1/tag_groups/${group.id}`, { headers: { Authorization: this.authToken } })
          .then(() => this.loadTagGroups())
          .catch((error) => { this.$notify(this.$t("errors.error"), error.response?.data?.error || error.message, "error"); });
      };
      this.showDeleteDialog = true;
    },

    openGroupColorPicker(group, event) {
      this.colorPickerGroup = group;
      this.colorMenuX = event ? event.clientX : 100;
      this.colorMenuY = event ? event.clientY : 100;
      this.groupColorMenu = true;
    },

    // === Tag CRUD ===
    startAddTag(group) {
      this.addingTagGroupId = group.id;
      this.newTagName = "";
      this.$nextTick(() => {
        const inputs = this.$refs.newTagInput;
        if (inputs && inputs.length) inputs[0].focus();
      });
    },

    cancelAddTag() {
      this.addingTagGroupId = null;
      this.newTagName = "";
      this.submittingTag = false;
    },

    submitNewTag(group) {
      if (this.submittingTag) return;
      if (!this.newTagName.trim()) {
        this.cancelAddTag();
        return;
      }
      this.submittingTag = true;
      this.axios
        .post(`/api/v1/tag_groups/${group.id}/tags`, {
          name: this.newTagName.trim(),
          color: group.color,
          sort_order: (group.tags || []).length,
        }, { headers: { Authorization: this.authToken } })
        .then(() => {
          this.cancelAddTag();
          this.loadTagGroups();
        })
        .catch((error) => {
          this.$notify(this.$t("errors.error"), error.response?.data?.errors?.join(", ") || error.message, "error");
        });
    },

    editTag(group, tag) {
      this.editingTag = { ...tag };
      this.editingTagGroupId = group.id;
      this.showEditTagDialog = true;
    },

    submitEditTag() {
      const t = this.editingTag;
      this.axios
        .patch(`/api/v1/tag_groups/${this.editingTagGroupId}/tags/${t.id}`, {
          name: t.name, color: t.color, sort_order: t.sort_order,
        }, { headers: { Authorization: this.authToken } })
        .then(() => {
          this.showEditTagDialog = false;
          this.loadTagGroups();
        })
        .catch((error) => {
          this.$notify(this.$t("errors.error"), error.response?.data?.errors?.join(", ") || error.message, "error");
        });
    },

    confirmDeleteTag(group, tag) {
      this.deleteDialogTitle = this.$t("tags.confirm_delete_tag");
      this.deleteCallback = () => {
        this.axios
          .delete(`/api/v1/tag_groups/${group.id}/tags/${tag.id}`, { headers: { Authorization: this.authToken } })
          .then(() => this.loadTagGroups())
          .catch((error) => { this.$notify(this.$t("errors.error"), error.response?.data?.error || error.message, "error"); });
      };
      this.showDeleteDialog = true;
    },

    confirmDelete() {
      if (this.deleteCallback) this.deleteCallback();
      this.showDeleteDialog = false;
      this.deleteCallback = null;
    },
  },
};
</script>

<style scoped>
.tag-group-card {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.2s;
}
.tag-group-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.tag-group-card__header {
  background: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.theme--dark .tag-group-card {
  border-color: rgba(255, 255, 255, 0.1);
}
.theme--dark .tag-group-card__header {
  background: rgba(255, 255, 255, 0.03);
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.tag-group-card__color-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.15s;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px rgba(0,0,0,0.06);
}
.tag-group-card__color-dot:hover {
  transform: scale(1.3);
}

.tag-chip {
  cursor: pointer;
  font-weight: 500;
  letter-spacing: 0.01em;
  transition: transform 0.1s, box-shadow 0.1s;
}
.tag-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.tag-inline-input {
  max-width: 130px;
  font-size: 13px;
  margin: 0 !important;
  padding: 0 !important;
}
.tag-inline-input >>> .v-input__control {
  min-height: 24px !important;
}
.tag-inline-input >>> .v-input__slot {
  min-height: 24px !important;
  max-height: 24px !important;
  padding: 0 10px !important;
  margin: 0 !important;
  border: 1.5px dashed rgba(25, 118, 210, 0.5) !important;
  border-radius: 16px !important;
  background: rgba(25, 118, 210, 0.04) !important;
}
.tag-inline-input >>> input {
  padding: 0 !important;
  max-height: 24px !important;
}
.theme--dark .tag-inline-input >>> .v-input__slot {
  border-color: rgba(100, 181, 246, 0.5) !important;
  background: rgba(100, 181, 246, 0.08) !important;
}

.tag-add-btn {
  text-transform: none;
  letter-spacing: 0;
  opacity: 0.7;
  transition: opacity 0.15s;
}
.tag-add-btn:hover {
  opacity: 1;
}

.color-swatch {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  box-shadow: 0 0 0 2px transparent;
}
.color-swatch:hover {
  transform: scale(1.15);
}
.color-swatch--active {
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px currentColor;
  transform: scale(1.1);
}
</style>
